import React, { FC } from 'react'
import { calculateTimeLeft } from '@/core/timeHelper'
import { Box, Flex, Grid } from '@/components/atoms/Grid'
import styled, { useTheme } from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import useTimeCounter from 'hooks/useTimeCounter'
import { CountdownClockDefault } from '@/components/atoms/icons/CountdownClockDefault'

type ProductTimeCounterProps = {
  startTime: Date
  endTime: Date
  transparent?: boolean
  valueProps?: Rest
  isCampaignCounter?: boolean
} & Rest

const ProductTimeCounter: FC<ProductTimeCounterProps> = ({
  startTime,
  endTime,
  transparent = false,
  valueProps,
  isCampaignCounter,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const time = useTimeCounter(startTime, endTime)
  const theme = useTheme()

  if (!time.startTime || !time.endTime) {
    return null
  }

  const calculatedTimeLeft = calculateTimeLeft(time.currentTime, time.endTime)
  const timeLeft = Object.keys(calculatedTimeLeft).map((key) => ({
    count: calculatedTimeLeft[key],
    unit: t(`Time.${key}`, { count: calculatedTimeLeft[key] })
  }))

  return (
    <Box {...rest} transparent={transparent} data-testid="timeCounter">
      <TimeContainer {...valueProps}>
        <CountdownClockDefault
          color={theme.colors.background}
          pl="5px"
          width={isCampaignCounter ? 32 : 16}
          height={isCampaignCounter ? 32 : 16}
        />
        <Flex alignItems="center">
          {timeLeft.map(({ count, unit }, offset) => (
            <React.Fragment key={offset}>
              <TimerCounter
                textAlign={'center'}
                suppressHydrationWarning
                isCampaignCounter={isCampaignCounter}
              >
                {count}
              </TimerCounter>
              {offset === 0 ? (
                <>
                  &nbsp;
                  <TimerCounter
                    width={'auto'}
                    suppressHydrationWarning
                    isCampaignCounter={isCampaignCounter}
                  >
                    {unit}
                  </TimerCounter>
                  &nbsp;
                </>
              ) : offset < timeLeft.length - 1 ? (
                <TimerPoints width={'auto'} suppressHydrationWarning>
                  <>&#58;</>
                </TimerPoints>
              ) : null}
            </React.Fragment>
          ))}
        </Flex>
      </TimeContainer>
    </Box>
  )
}

export default React.memo(ProductTimeCounter)

const TimeContainer = styled(Grid)`
  padding-left: 0;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto;
  align-items: center;
  grid-gap: 5px;
`

const TimerCounter = styled.span`
  font-weight: 600;
  font-size: ${(props) => (props.isCampaignCounter ? '24px' : '12px')};
`
const TimerPoints = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 0 2px;
`
